import React, {
  FC, useCallback, useMemo, useState,
} from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';
import { useModalsContext, useToggleModalsContext } from '../../contexts/ModalsContext';
import useMediaQuery from '../../hooks/useMediaQuery';
import { FormType, QuickQuoteInitialData } from '../QuickQuoteForm/types';
import Modal from '../Modal/Modal';
import grid from '../../../css/grid.module.scss';
import styles from './QuickQuoteModal.module.scss';

const QuickQuoteForm = loadable(
  () => import(/* webpackChunkName: 'quickQuoteForm' */'../QuickQuoteForm/QuickQuoteForm'),
);

interface QuickQuoteModalProps {
  quickQuote: QuickQuoteInitialData;
}

// eslint-disable-next-line
const globalAny: any = global; // TODO:  hack to add properties to Global object

const QuickQuoteModal: FC<QuickQuoteModalProps> = ({
  quickQuote,
}: QuickQuoteModalProps) => {
  const isMobile = useMediaQuery({ maxBreakpoint: 'xs' });
  const toggleModal = useToggleModalsContext();
  const { modalsState, properties } = useModalsContext();
  const { isQuickQuoteModalOpen: isOpen } = modalsState;

  const [isClosureTriggered, setIsClosureTriggered] = useState(false); // IO2702

  // TODO:  hack! remove after legacy pages migrate to React
  globalAny.toggleReactModal = toggleModal;
  // TODO: hack! remove after legacy pages migrate to React
  globalAny.modalsState = modalsState;

  const cancelClosure = useCallback(() => {
    setIsClosureTriggered(false);
  }, []);

  const close = useCallback(() => {
    setIsClosureTriggered(false);
    toggleModal({ isQuickQuoteModalOpen: false });
  }, [toggleModal]);

  const titles = useMemo(() => ({
    [FormType.BookViewing]: quickQuote.bookAViewingLabel,
    [FormType.QuickQuote]: quickQuote.quickQuoteLabel,
    [FormType.LargeTeams]: quickQuote.quickQuoteLabel,
    [FormType.GeneralEnquiry]: quickQuote.quickQuoteLabel,
  }), [quickQuote.bookAViewingLabel, quickQuote.quickQuoteLabel]);

  if (!isOpen) {
    return null;
  }

  const type = properties.formType || FormType.QuickQuote;

  const title = (isMobile && properties?.title) || titles[type as keyof typeof titles];

  const containerClasses = {
    [styles.container]: true,
    [styles.isCompactOnMobile]: properties?.isCompactOnMobile,
  };

  return (
    <Modal
      classes={{
        overlay: classNames(grid.container, grid.containerFixed, styles.overlay),
        container: classNames(containerClasses),
        title: classNames(styles.title),
      }}
      closeHandler={close}
      isWithoutCloseButton={isClosureTriggered}
      title={title}
    >
      <QuickQuoteForm
        {...properties}
        cancelClosureCallback={cancelClosure}
        closeCallback={close}
        initialData={quickQuote}
        isClosureTriggered={isClosureTriggered}
        isModal
      />
    </Modal>
  );
};
QuickQuoteModal.displayName = 'QuickQuoteModal';

export default QuickQuoteModal;
